import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "../../api/api";
import { toast } from "react-toastify";
import SEOSection from "../../components/SEOSection/SEOSection";

const iconOptions = [
  { value: "fa fa-plane", label: "Plane (fa-plane)" },
  { value: "fas fa-hotel", label: "Hotel (fa-hotel)" },
  { value: "fa fa-bus", label: "Bus (fa-bus)" },
  { value: "fa fa-car", label: "Car (fa-car)" },
  { value: "fa fa-map-marker", label: "Map (fa-map)" },
  { value: "fas fa-map-marker-alt", label: "Map Marker (fa-map-marker-alt)" },
  { value: "fas fa-hiking", label: "Hiking (fa-hiking)" },
  { value: "fa-solid fa-campground", label: "Campground (fa-campground)" },
  {
    value: "fa-solid fa-umbrella-beach",
    label: "Umbrella Beach (fa-umbrella-beach)",
  },
  { value: "fa-solid fa-suitcase", label: "Suitcase (fa-suitcase-rolling)" },
  { value: "fas fa-ticket-alt", label: "Ticket (fa-ticket-alt)" },
  { value: "fa fa-binoculars", label: "Binoculars (fa-binoculars)" },
  { value: "fa fa-compass", label: "Compass (fa-compass)" },
  { value: "fa fa-globe", label: "Globe (fa-globe)" },
  { value: "fa-solid fa-landmark", label: "Landmark (fa-landmark)" },
  { value: "fa-solid fa-monument", label: "Monument (fa-monument)" },
  { value: "fa-solid fa-mountain", label: "Mountain (fa-mountain)" },
  { value: "fa fa-ship", label: "Ship (fa-ship)" },
  { value: "fa fa-train", label: "Train (fa-train)" },
  { value: "fa fa-subway", label: "Subway (fa-subway)" },
];

const fieldConfigs = {
  title: {
    type: "title",
    label: "Title",
    placeholder: "Enter tour title",
    isTextarea: false,
  },
  description: {
    type: "description",
    label: "Description",
    placeholder: "Enter description or tour benefits",
    isTextarea: true,
  },
  galleryImage: {
    type: "galleryImage",
    label: "Gallery Image",
    placeholder: "Enter image URL",
    isTextarea: false,
  },
  location: {
    type: "location",
    label: "Location",
    placeholder: "Enter tour location (e.g., Melbourne, Australia)",
    isTextarea: false,
  },
  duration: {
    type: "duration",
    label: "Duration",
    placeholder: "Enter tour duration (e.g., 3days - 2 night)",
    isTextarea: false,
  },
  groupSize: {
    type: "groupSize",
    label: "Group Size",
    placeholder: "Enter group size (e.g., 05-13 persons)",
    isTextarea: false,
  },
  ratings: {
    type: "ratings",
    label: "Ratings",
    placeholder: "Enter average rating (0-5)",
    isTextarea: false,
  },
  tripInfo: {
    type: "tripInfo",
    label: "Trip Information",
    placeholder: "Enter trip info value",
    isTextarea: false,
  },
  includedService: {
    type: "includedService",
    label: "Included Service",
    placeholder: "Enter included service",
    isTextarea: false,
  },
  excludedService: {
    type: "excludedService",
    label: "Excluded Service",
    placeholder: "Enter excluded service",
    isTextarea: false,
  },
  itinerary: {
    type: "itinerary",
    label: "Itinerary",
    placeholder: "Enter itinerary title",
    isTextarea: false,
  },
  faq: {
    type: "faq",
    label: "FAQ",
    placeholder: "Enter question",
    isTextarea: false,
  },
  pricing: {
    type: "pricing",
    label: "Pricing",
    placeholder: "Enter price",
    isTextarea: false,
  },
  extraService: {
    type: "extraService",
    label: "Extra Service",
    placeholder: "Enter extra service name",
    isTextarea: false,
  },
  socialLink: {
    type: "socialLink",
    label: "Social Link",
    placeholder: "Enter social link URL",
    isTextarea: false,
  },
  mapEmbedUrl: {
    type: "mapEmbedUrl",
    label: "Map Embed URL",
    placeholder: "Enter Google Maps embed URL",
    isTextarea: false,
  },
  checkList: {
    type: "checkList",
    label: "Check List Item",
    placeholder: "Enter check list item",
    isTextarea: false,
  },
  tourBenefits: {
    type: "tourBenefits",
    label: "Tour Benefit",
    placeholder: "Enter tour benefit",
    isTextarea: false,
  },
};

const FieldInput = ({
  id,
  type,
  label,
  index,
  placeholder,
  isTextarea,
  removeField,
  dragListeners,
  depth,
  data = {},
}) => (
  <div
    className={`mb-4 p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm ${
      depth > 0 ? "border-primary-500" : ""
    }`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center">
        <div {...dragListeners} className="cursor-grab mr-2 text-gray-500">
          ≡
        </div>
        <span className="font-semibold text-lg text-gray-800 dark:text-gray-200">
          {label} {index !== null ? index + 1 : ""}
        </span>
      </div>
      <button
        type="button"
        onClick={removeField}
        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
      >
        Remove
      </button>
    </div>
    {type === "faq" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Question
        </label>
        <input
          type="text"
          id={`question-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data?.question || ""}
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Answer
        </label>
        <textarea
          id={`answer-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter answer"
          defaultValue={data?.answer || ""}
        />
        <div className="mt-3">
          <label
            htmlFor={`file-faq-${id}`}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Upload FAQ Image (optional)
          </label>
          <input
            type="file"
            accept="image/*"
            id={`file-faq-${id}`}
            name={`file-faq-${id}`}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200"
          />
        </div>
        {data?.image?.url &&
          typeof data.image.url === "string" &&
          data.image.url.startsWith("/Uploads/") && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Preview
              </label>
              <img
                src={`${process.env.REACT_APP_BACKEND_API_URL}${data.image.url}`}
                alt="Preview"
                style={{ maxWidth: "200px" }}
                onError={(e) => (e.target.src = "/fallback-image.png")}
              />
            </div>
          )}
      </>
    ) : type === "galleryImage" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Image URL (optional)
        </label>
        <input
          type="url"
          id={`url-${type}-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={`${placeholder} (optional)`}
          defaultValue={
            data?.url && !data.url.startsWith("/Uploads/") ? data.url : ""
          }
        />
        <div className="mt-3">
          <label
            htmlFor={`file-${type}-${id}`}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Or Upload Image (optional)
          </label>
          <input
            type="file"
            accept="image/*"
            id={`file-${type}-${id}`}
            name={`file-${type}-${id}`}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200"
          />
        </div>
        {data?.url && data.url.startsWith("/Uploads/") && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Preview
            </label>
            <img
              src={`${process.env.REACT_APP_BACKEND_API_URL}${data.url}`}
              alt="Preview"
              style={{ maxWidth: "200px" }}
              onError={(e) => (e.target.src = "/fallback-image.png")}
            />
          </div>
        )}
      </>
    ) : type === "tripInfo" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Type
        </label>
        <select
          id={`type-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          defaultValue={data?.type || ""}
        >
          <option value="">Select Type</option>
          {[
            "accommodation",
            "tourAvailability",
            "tripGuide",
            "paymentMethod",
            "transportation",
            "activities",
            "groupSize",
            "tripType",
            "wifi",
            "freeCancel",
            "insurance",
            "departureCity",
          ].map((opt) => (
            <option key={opt} value={opt}>
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </option>
          ))}
        </select>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Title
        </label>
        <input
          type="text"
          id={`title-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter title (e.g., Accommodation)"
          defaultValue={data?.title || ""}
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Value
        </label>
        <input
          type="text"
          id={`value-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data?.value || ""}
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Icon (optional)
        </label>
        <select
          id={`icon-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          defaultValue={data?.icon || ""}
        >
          <option value="">Select Icon</option>
          {iconOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </>
    ) : type === "includedService" || type === "excludedService" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name
        </label>
        <input
          type="text"
          id={`name-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data?.name || ""}
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Icon (optional)
        </label>
        <select
          id={`icon-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          defaultValue={data?.icon || ""}
        >
          <option value="">Select Icon</option>
          {iconOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </>
    ) : type === "itinerary" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Day
        </label>
        <input
          type="number"
          id={`day-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter day number"
          defaultValue={data?.day || ""}
          min="1"
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Title
        </label>
        <input
          type="text"
          id={`title-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data?.title || ""}
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Description
        </label>
        <textarea
          id={`description-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter itinerary description"
          defaultValue={data?.description || ""}
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Activities (comma-separated)
        </label>
        <textarea
          id={`activities-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter activities (e.g., Airport pickup, Local markets)"
          defaultValue={
            Array.isArray(data?.activities) ? data.activities.join(", ") : ""
          }
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Icon (optional)
        </label>
        <select
          id={`icon-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          defaultValue={data?.icon || ""}
        >
          <option value="">Select Icon</option>
          {iconOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </>
    ) : type === "pricing" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Price
        </label>
        <input
          type="number"
          id={`price-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data?.price || ""}
          min="0"
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Discount (%)
        </label>
        <input
          type="number"
          id={`discount-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter discount percentage (0-100)"
          defaultValue={data?.discount || 0}
          min="0"
          max="100"
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Type
        </label>
        <select
          id={`type-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          defaultValue={data?.type || ""}
        >
          <option value="">Select Type</option>
          <option value="adult">Adult</option>
          <option value="child">Child</option>
        </select>
      </>
    ) : type === "extraService" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name
        </label>
        <input
          type="text"
          id={`name-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data?.name || ""}
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Type
        </label>
        <select
          id={`type-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          defaultValue={data?.type || ""}
        >
          <option value="">Select Type</option>
          <option value="perBooking">Per Booking</option>
          <option value="perPerson">Per Person</option>
        </select>
      </>
    ) : type === "socialLink" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Platform
        </label>
        <select
          id={`platform-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          defaultValue={data?.platform || ""}
        >
          <option value="">Select Platform</option>
          <option value="facebook">Facebook</option>
          <option value="twitter">Twitter</option>
          <option value="linkedin">LinkedIn</option>
          <option value="instagram">Instagram</option>
        </select>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          URL
        </label>
        <input
          type="url"
          id={`url-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data?.url || ""}
        />
      </>
    ) : type === "ratings" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Average Rating
        </label>
        <input
          type="number"
          id={`average-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter average rating (0-5)"
          defaultValue={data?.average || ""}
          min="0"
          max="5"
          step="0.1"
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Total Reviews
        </label>
        <input
          type="number"
          id={`totalReviews-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter total reviews"
          defaultValue={data?.totalReviews || 0}
          min="0"
        />
      </>
    ) : type === "description" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Title (optional)
        </label>
        <input
          type="text"
          id={`title-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter description title"
          defaultValue={data?.title || ""}
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Data
        </label>
        <textarea
          id={`data-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data?.data || ""}
        />
      </>
    ) : type === "checkList" || type === "tourBenefits" ? (
      <input
        type="text"
        id={`${type}-${id}`}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
        placeholder={placeholder}
        defaultValue={typeof data === "string" ? data : ""}
      />
    ) : isTextarea ? (
      <textarea
        id={`${type}-${id}`}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
        placeholder={placeholder}
        defaultValue={typeof data === "string" ? data : ""}
      />
    ) : (
      <input
        type="text"
        id={`${type}-${id}`}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
        placeholder={placeholder}
        defaultValue={typeof data === "string" ? data : ""}
      />
    )}
  </div>
);

const TreeItem = ({
  item,
  depth,
  removeField,
  allFieldsItems,
  activeId,
  projected,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const isUnique = [
    "title",
    "location",
    "duration",
    "groupSize",
    "mapEmbedUrl",
  ].includes(item.type);
  const sameTypeFields = allFieldsItems.filter((f) => f.type === item.type);
  const index = isUnique
    ? null
    : sameTypeFields.findIndex((f) => f.id === item.id);
  const currentDepth =
    activeId === item.id && projected ? projected.depth : depth;

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="mb-2">
      <FieldInput
        id={item.id}
        type={item.type}
        label={item.label}
        index={index}
        placeholder={item.placeholder}
        isTextarea={item.isTextarea}
        removeField={() => removeField(item.id)}
        dragListeners={listeners}
        depth={currentDepth}
        data={item.data || {}}
      />
      {Array.isArray(item.children) && item.children.length > 0 && (
        <div className="ml-6">
          <SortableContext
            id={`sortable-${item.id}`}
            items={item.children.map((child) => child.id)}
            strategy={verticalListSortingStrategy}
          >
            {item.children.map((child) => (
              <TreeItem
                key={child.id}
                item={child}
                depth={depth + 1}
                removeField={removeField}
                allFieldsItems={allFieldsItems}
                activeId={activeId}
                projected={projected}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

function flattenTree(items, parent = null, depth = 0) {
  return items.reduce((acc, item) => {
    const newItem = { ...item, parent, depth };
    acc.push(newItem);
    if (item.children && item.children.length > 0) {
      acc.push(...flattenTree(item.children, item.id, depth + 1));
    }
    return acc;
  }, []);
}

function buildTree(flattenedItems) {
  const idMapping = flattenedItems.reduce((acc, item) => {
    acc[item.id] = {
      id: item.id,
      type: item.type,
      label: item.label,
      placeholder: item.placeholder,
      isTextarea: item.isTextarea,
      data: item.data,
      children: [],
    };
    return acc;
  }, {});

  const root = [];

  flattenedItems.forEach((item) => {
    const node = idMapping[item.id];
    if (item.parent === null) {
      root.push(node);
    } else {
      const parentNode = idMapping[item.parent];
      if (parentNode) {
        parentNode.children.push(node);
      }
    }
  });

  return root;
}

function getProjection(
  items,
  activeId,
  overId,
  dragOffset,
  indentationWidth = 20,
) {
  const overItemIndex = items.findIndex((i) => i.id === overId);
  const activeItemIndex = items.findIndex((i) => i.id === activeId);

  if (overItemIndex === -1 || activeItemIndex === -1) return null;

  const activeItem = items[activeItemIndex];
  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];

  const dragDepth = Math.round(dragOffset / indentationWidth);
  const tentativeDepth = Math.max(0, activeItem.depth + dragDepth);

  const maxDepth = getMaxDepth(previousItem);
  const minDepth = getMinDepth(nextItem);

  let depth = Math.max(minDepth, Math.min(maxDepth, tentativeDepth));

  let parentId;
  if (depth === 0) {
    parentId = null;
  } else {
    parentId = getParentId(previousItem, depth, newItems);
  }

  return { depth, maxDepth, minDepth, parentId };
}

function getMaxDepth(previousItem) {
  return previousItem ? previousItem.depth + 1 : 0;
}

function getMinDepth(nextItem) {
  return nextItem ? nextItem.depth : 0;
}

function getParentId(previousItem, depth, items) {
  if (!previousItem) return null;

  let current = previousItem;
  let currentIndex = items.findIndex((i) => i.id === current.id);

  while (current && current.depth >= depth && currentIndex > 0) {
    currentIndex -= 1;
    current = items[currentIndex];
  }

  return current ? current.id : null;
}

function removeFromTree(items, id) {
  return items.filter((item) => {
    if (item.id === id) return false;
    if (item.children) {
      item.children = removeFromTree(item.children, id);
    }
    return true;
  });
}

function getAllFields(items) {
  return items.reduce(
    (acc, item) => [...acc, item, ...getAllFields(item.children || [])],
    [],
  );
}

function hydrateContent(content, generateId) {
  if (!Array.isArray(content)) return [];
  return content
    .map((node) => {
      const config = fieldConfigs[node.type];
      if (!config) return null;
      let data;
      if (node.type === "faq") {
        data = node.data || { question: "", answer: "", image: { url: null } };
      } else if (node.type === "galleryImage") {
        data = node.data || { url: "" };
      } else if (node.type === "tripInfo") {
        data = node.data || { type: "", title: "", value: "", icon: "" };
      } else if (
        node.type === "includedService" ||
        node.type === "excludedService"
      ) {
        data = node.data || { name: "", icon: "" };
      } else if (node.type === "itinerary") {
        data = node.data || {
          day: "",
          title: "",
          description: "",
          activities: [],
          icon: "",
        };
      } else if (node.type === "pricing") {
        data = node.data || { price: "", discount: 0, type: "" };
      } else if (node.type === "extraService") {
        data = node.data || { name: "", type: "" };
      } else if (node.type === "socialLink") {
        data = node.data || { platform: "", url: "" };
      } else if (node.type === "ratings") {
        data = node.data || { average: "", totalReviews: 0 };
      } else if (node.type === "description") {
        data = node.data || { title: "", data: "" };
      } else if (node.type === "checkList" || node.type === "tourBenefits") {
        data = node.data || "";
      } else {
        data = node.data || "";
      }
      return {
        id: generateId(),
        ...config,
        data,
        children: node.children
          ? hydrateContent(node.children, generateId)
          : [],
      };
    })
    .filter(Boolean);
}

export default function AddTour({
  isEdit = false,
  tourId = null,
  onSuccess = () => {},
}) {
  const generateId = () =>
    crypto.randomUUID?.() || Math.random().toString(36).substr(2, 9);
  const [fields, setFields] = useState({ allFields: [] });
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [projected, setProjected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seoData, setSeoData] = useState({
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    ogImage: "",
  });

  const handleSeoChange = (e) => {
    const { name, value } = e.target;
    setSeoData((prev) => ({ ...prev, [name]: value }));
  };

  const flattenedFields = useMemo(
    () => flattenTree(fields.allFields),
    [fields.allFields],
  );
  const allFieldsItems = useMemo(
    () => getAllFields(fields.allFields),
    [fields.allFields],
  );

  useEffect(() => {
    if (isEdit && tourId) {
      fetchTourById(tourId);
    } else {
      const defaultFields = Object.keys(fieldConfigs).map((key) => {
        const config = fieldConfigs[key];
        let data;
        if (key === "faq") {
          data = { question: "", answer: "", image: { url: null } };
        } else if (key === "galleryImage") {
          data = { url: "" };
        } else if (key === "tripInfo") {
          data = { type: "", title: "", value: "", icon: "" };
        } else if (key === "includedService" || key === "excludedService") {
          data = { name: "", icon: "" };
        } else if (key === "itinerary") {
          data = {
            day: "",
            title: "",
            description: "",
            activities: [],
            icon: "",
          };
        } else if (key === "pricing") {
          data = { price: "", discount: 0, type: "" };
        } else if (key === "extraService") {
          data = { name: "", type: "" };
        } else if (key === "socialLink") {
          data = { platform: "", url: "" };
        } else if (key === "ratings") {
          data = { average: "", totalReviews: 0 };
        } else if (key === "description") {
          data = { title: "", data: "" };
        } else if (key === "checkList" || key === "tourBenefits") {
          data = "";
        } else {
          data = "";
        }
        return {
          id: generateId(),
          ...config,
          data,
          children: [],
        };
      });
      setFields({ allFields: defaultFields });
    }
  }, [isEdit, tourId]);

  const fetchTourById = async (id) => {
    setLoading(true);
    try {
      const resp = await axios.get(`/api/tour/${id}`);
      const tourObj = resp?.data?.data || resp?.data;
      console.log("Fetched tourObj:", tourObj); // Debug log
      if (tourObj) {
        const content = [
          { type: "title", data: tourObj.title || "" },
          ...(tourObj.description?.map((desc) => ({
            type: "description",
            data: desc,
          })) || []),
          ...(tourObj.galleryImages?.map((img) => ({
            type: "galleryImage",
            data: img,
          })) || []),
          { type: "location", data: tourObj.location || "" },
          { type: "duration", data: tourObj.duration || "" },
          { type: "groupSize", data: tourObj.groupSize || "" },
          {
            type: "ratings",
            data: tourObj.ratings || { average: 0, totalReviews: 0 },
          },
          ...(tourObj.tripInfo?.map((info) => ({
            type: "tripInfo",
            data: info,
          })) || []),
          ...(tourObj.includedServices?.map((service) => ({
            type: "includedService",
            data: service,
          })) || []),
          ...(tourObj.excludedServices?.map((service) => ({
            type: "excludedService",
            data: service,
          })) || []),
          ...(tourObj.itinerary?.map((item) => ({
            type: "itinerary",
            data: item,
          })) || []),
          ...(tourObj.faqs?.map((faq) => ({ type: "faq", data: faq })) || []),
          ...(tourObj.pricing?.map((price) => ({
            type: "pricing",
            data: price,
          })) || []),
          ...(tourObj.extraServices?.map((service) => ({
            type: "extraService",
            data: service,
          })) || []),
          ...(tourObj.socialLinks?.map((link) => ({
            type: "socialLink",
            data: link,
          })) || []),
          { type: "mapEmbedUrl", data: tourObj.mapEmbedUrl || "" },
          ...(tourObj.checkList?.map((item) => ({
            type: "checkList",
            data: item,
          })) || []),
          ...(tourObj.tourBenefits?.map((item) => ({
            type: "tourBenefits",
            data: item,
          })) || []),
        ].filter(Boolean);
        const hydrated = hydrateContent(content, generateId);
        setFields({ allFields: hydrated });
        setSeoData({
          metaTitle: tourObj.metaTitle || "",
          metaDescription: tourObj.metaDescription || "",
          metaKeywords: tourObj.metaKeywords || "",
          ogImage: tourObj.ogImage || "",
        });
      } else {
        toast.error("Invalid tour data to edit");
        setFields({
          allFields: Object.keys(fieldConfigs).map((key) => {
            const config = fieldConfigs[key];
            let data =
              key === "ratings"
                ? { average: 0, totalReviews: 0 }
                : key === "description"
                  ? { title: "", data: "" }
                  : key === "faq"
                    ? { question: "", answer: "", image: { url: "" } }
                    : key === "galleryImage"
                      ? { url: "" }
                      : key === "tripInfo"
                        ? { type: "", title: "", value: "", icon: "" }
                        : key === "includedService" || key === "excludedService"
                          ? { name: "", icon: "" }
                          : key === "itinerary"
                            ? {
                                day: "",
                                title: "",
                                description: "",
                                activities: [],
                                icon: "",
                              }
                            : key === "pricing"
                              ? { price: "", discount: 0, type: "" }
                              : key === "extraService"
                                ? { name: "", type: "" }
                                : key === "socialLink"
                                  ? { platform: "", url: "" }
                                  : "";
            return { id: generateId(), ...config, data, children: [] };
          }),
        });
      }
    } catch (err) {
      console.error("Error fetching tour:", err);
      toast.error("Failed to load tour data");
    } finally {
      setLoading(false);
    }
  };

  const removeField = (id) => {
    setFields((prev) => ({
      allFields: removeFromTree(prev.allFields, id),
    }));
  };

  const areAllFieldsRemoved = () => fields.allFields.length === 0;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
    setOverId(active.id);
    setOffsetLeft(0);
  };

  const handleDragMove = ({ delta, over }) => {
    setOffsetLeft(delta.x);
    if (over) {
      setOverId(over.id);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (projected && over) {
      const { depth, parentId } = projected;
      const cloned = [...flattenedFields];
      const overIndex = cloned.findIndex((i) => i.id === over.id);
      const activeIndex = cloned.findIndex((i) => i.id === active.id);
      const activeTreeItem = cloned[activeIndex];
      cloned[activeIndex] = { ...activeTreeItem, depth, parent: parentId };
      const newItems = arrayMove(cloned, activeIndex, overIndex);
      setFields((prev) => ({
        allFields: buildTree(newItems),
      }));
    }
    setActiveId(null);
    setOverId(null);
    setProjected(null);
    setOffsetLeft(0);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
    setProjected(null);
    setOffsetLeft(0);
  };

  useEffect(() => {
    if (activeId && overId) {
      setProjected(
        getProjection(flattenedFields, activeId, overId, offsetLeft, 20),
      );
    } else {
      setProjected(null);
    }
  }, [activeId, overId, offsetLeft, flattenedFields]);

  const addField = (fieldType) => {
    setFields((prev) => {
      const config = fieldConfigs[fieldType];
      if (!config) return prev;
      if (
        [
          "title",
          "location",
          "duration",
          "groupSize",
          "mapEmbedUrl",
          "ratings",
        ].includes(fieldType)
      ) {
        if (
          getAllFields(prev.allFields).some((item) => item.type === fieldType)
        ) {
          toast.warn(`Only one ${config.label} is allowed`);
          return prev;
        }
      }
      const newId = generateId();
      let newField;
      if (fieldType === "faq") {
        newField = {
          id: newId,
          ...config,
          data: { question: "", answer: "", image: { url: "" } },
          children: [],
        };
      } else if (fieldType === "galleryImage") {
        newField = { id: newId, ...config, data: { url: "" }, children: [] };
      } else if (fieldType === "tripInfo") {
        newField = {
          id: newId,
          ...config,
          data: { type: "", title: "", value: "", icon: "" },
          children: [],
        };
      } else if (
        fieldType === "includedService" ||
        fieldType === "excludedService"
      ) {
        newField = {
          id: newId,
          ...config,
          data: { name: "", icon: "" },
          children: [],
        };
      } else if (fieldType === "itinerary") {
        newField = {
          id: newId,
          ...config,
          data: {
            day: "",
            title: "",
            description: "",
            activities: [],
            icon: "",
          },
          children: [],
        };
      } else if (fieldType === "pricing") {
        newField = {
          id: newId,
          ...config,
          data: { price: "", discount: 0, type: "" },
          children: [],
        };
      } else if (fieldType === "extraService") {
        newField = {
          id: newId,
          ...config,
          data: { name: "", type: "" },
          children: [],
        };
      } else if (fieldType === "socialLink") {
        newField = {
          id: newId,
          ...config,
          data: { platform: "", url: "" },
          children: [],
        };
      } else if (fieldType === "ratings") {
        newField = {
          id: newId,
          ...config,
          data: { average: "", totalReviews: 0 },
          children: [],
        };
      } else if (fieldType === "description") {
        newField = {
          id: newId,
          ...config,
          data: { title: "", data: "" },
          children: [],
        };
      } else if (fieldType === "checkList" || fieldType === "tourBenefits") {
        newField = { id: newId, ...config, data: "", children: [] };
      } else {
        newField = { id: newId, ...config, data: "", children: [] };
      }
      return { allFields: [...prev.allFields, newField] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const contentCopy = JSON.parse(JSON.stringify(fields.allFields));
    const formData = new FormData();

    const traverseSetValues = (nodes) => {
      return nodes.map((node) => {
        const newNode = { type: node.type };
        if (
          [
            "title",
            "location",
            "duration",
            "groupSize",
            "mapEmbedUrl",
          ].includes(node.type)
        ) {
          const el = document.getElementById(`${node.type}-${node.id}`);
          newNode.data = el?.value?.trim() || "";
        } else if (node.type === "description") {
          const titleEl = document.getElementById(`title-${node.id}`);
          const dataEl = document.getElementById(`data-${node.id}`);
          const title = titleEl?.value?.trim() || "";
          const dataValue = dataEl?.value?.trim() || "";
          newNode.data = { title, data: dataValue };
        } else if (node.type === "galleryImage") {
          const urlEl = document.getElementById(`url-${node.type}-${node.id}`);
          const fileInput = document.getElementById(
            `file-${node.type}-${node.id}`,
          );
          if (urlEl?.value?.trim()) {
            newNode.data = { url: urlEl.value.trim() };
          } else if (fileInput?.files?.[0]) {
            formData.append(`galleryImage-${node.id}`, fileInput.files[0]);
            newNode.data = { id: node.id, url: "" };
          } else {
            newNode.data = node.data || { id: node.id, url: "" };
          }
        } else if (node.type === "ratings") {
          const avgEl = document.getElementById(`average-${node.id}`);
          const totalEl = document.getElementById(`totalReviews-${node.id}`);
          const average = parseFloat(avgEl?.value?.trim()) || 0;
          const totalReviews = parseInt(totalEl?.value?.trim()) || 0;
          newNode.data = { average, totalReviews };
        } else if (node.type === "tripInfo") {
          const typeEl = document.getElementById(`type-${node.id}`);
          const titleEl = document.getElementById(`title-${node.id}`);
          const valueEl = document.getElementById(`value-${node.id}`);
          const iconEl = document.getElementById(`icon-${node.id}`);
          const type = typeEl?.value?.trim() || "";
          const title = titleEl?.value?.trim() || "";
          const value = valueEl?.value?.trim() || "";
          const icon = iconEl?.value?.trim() || "";
          newNode.data = { type, title, value, icon };
        } else if (
          node.type === "includedService" ||
          node.type === "excludedService"
        ) {
          const nameEl = document.getElementById(`name-${node.id}`);
          const iconEl = document.getElementById(`icon-${node.id}`);
          const name = nameEl?.value?.trim() || "";
          const icon = iconEl?.value?.trim() || "";
          newNode.data = { name, icon };
        } else if (node.type === "itinerary") {
          const dayEl = document.getElementById(`day-${node.id}`);
          const titleEl = document.getElementById(`title-${node.id}`);
          const descEl = document.getElementById(`description-${node.id}`);
          const activitiesEl = document.getElementById(`activities-${node.id}`);
          const iconEl = document.getElementById(`icon-${node.id}`);
          const day = parseInt(dayEl?.value?.trim()) || 0;
          const title = titleEl?.value?.trim() || "";
          const description = descEl?.value?.trim() || "";
          const activities =
            activitiesEl?.value
              ?.split(",")
              .map((a) => a.trim())
              .filter(Boolean) || [];
          const icon = iconEl?.value?.trim() || "";
          newNode.data = { day, title, description, activities, icon };
        } else if (node.type === "faq") {
          const qEl = document.getElementById(`question-${node.id}`);
          const aEl = document.getElementById(`answer-${node.id}`);
          const fileInput = document.getElementById(`file-faq-${node.id}`);
          const question = qEl?.value?.trim() || "";
          const answer = aEl?.value?.trim() || "";
          let image = null;
          if (fileInput?.files?.[0]) {
            formData.append(`faqImage-${node.id}`, fileInput.files[0]);
            image = { url: "" };
          } else if (node.data?.image?.url) {
            image = { url: node.data.image.url };
          }
          newNode.data = { id: node.id, question, answer, image };
        } else if (node.type === "pricing") {
          const priceEl = document.getElementById(`price-${node.id}`);
          const discountEl = document.getElementById(`discount-${node.id}`);
          const typeEl = document.getElementById(`type-${node.id}`);
          const price = parseFloat(priceEl?.value?.trim()) || 0;
          const discount = parseInt(discountEl?.value?.trim()) || 0;
          const type = typeEl?.value?.trim() || "";
          newNode.data = { price, discount, type };
        } else if (node.type === "extraService") {
          const nameEl = document.getElementById(`name-${node.id}`);
          const typeEl = document.getElementById(`type-${node.id}`);
          const name = nameEl?.value?.trim() || "";
          const type = typeEl?.value?.trim() || "";
          newNode.data = { name, type };
        } else if (node.type === "socialLink") {
          const platformEl = document.getElementById(`platform-${node.id}`);
          const urlEl = document.getElementById(`url-${node.id}`);
          const platform = platformEl?.value?.trim() || "";
          const url = urlEl?.value?.trim() || "";
          newNode.data = { platform, url };
        } else if (node.type === "checkList" || node.type === "tourBenefits") {
          const el = document.getElementById(`${node.type}-${node.id}`);
          newNode.data = el?.value?.trim() || "";
        }
        if (node.children?.length) {
          newNode.children = traverseSetValues(node.children);
        }
        return newNode;
      });
    };

    let dataNodes;
    try {
      dataNodes = traverseSetValues(contentCopy);
    } catch (err) {
      console.error("Validation error:", err);
      toast.error("Error processing form data");
      return;
    }

    const dataMap = {
      title: [],
      description: [],
      galleryImages: [],
      location: [],
      duration: [],
      groupSize: [],
      ratings: [],
      tripInfo: [],
      includedServices: [],
      excludedServices: [],
      itinerary: [],
      faqs: [],
      pricing: [],
      extraServices: [],
      socialLinks: [],
      mapEmbedUrl: [],
      checkList: [],
      tourBenefits: [],
    };

    dataNodes.forEach((node) => {
      const key =
        node.type === "includedService"
          ? "includedServices"
          : node.type === "excludedService"
            ? "excludedServices"
            : node.type === "galleryImage"
              ? "galleryImages"
              : node.type === "socialLink"
                ? "socialLinks"
                : node.type === "extraService"
                  ? "extraServices"
                  : node.type === "faq"
                    ? "faqs"
                    : node.type;
      dataMap[key].push(node.data);
    });

    const payload = {
      title: dataMap.title[0] || "",
      description: dataMap.description,
      galleryImages: dataMap.galleryImages,
      location: dataMap.location[0] || "",
      duration: dataMap.duration[0] || "",
      groupSize: dataMap.groupSize[0] || "",
      ratings: dataMap.ratings[0] || { average: 0, totalReviews: 0 },
      tripInfo: dataMap.tripInfo,
      includedServices: dataMap.includedServices,
      excludedServices: dataMap.excludedServices,
      itinerary: dataMap.itinerary,
      faqs: dataMap.faqs,
      pricing: dataMap.pricing,
      extraServices: dataMap.extraServices,
      socialLinks: dataMap.socialLinks,
      mapEmbedUrl: dataMap.mapEmbedUrl[0] || "",
      checkList: dataMap.checkList,
      tourBenefits: dataMap.tourBenefits,
      ...seoData,
    };

    formData.append("data", JSON.stringify(payload));

    try {
      let resp;
      if (isEdit) {
        resp = await axios.put(`/api/tour/${tourId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Tour updated successfully!");
      } else {
        resp = await axios.post("/api/tour/add-tour", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Tour added successfully!");
      }
      setFields({ allFields: [] });
      onSuccess();
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(
        `Failed to ${isEdit ? "update" : "add"} tour: ${err.message}`,
      );
    }
  };

  const activeItem = activeId
    ? flattenedFields.find((i) => i.id === activeId)
    : null;

  return (
    <div className="flex flex-1 bg-gray-100 dark:bg-gray-900">
      <div className="page-wrapper relative ml-auto w-[calc(100%-260px)] px-6 pt-24 duration-300">
        <div className="xl:w-full min-h-[calc(100vh-152px)] relative pb-14">
          <div className="grid md:grid-cols-12 lg:grid-cols-12 xl:grid-cols-12 gap-6 mb-6">
            <div className="sm:col-span-12 md:col-span-12 lg:col-span-8 xl:col-span-6 xl:col-start-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg w-full relative mb-6 shadow-md">
                <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6 dark:text-gray-300">
                  <h4 className="font-semibold text-xl flex-1 self-center mb-2 md:mb-0">
                    {isEdit ? "Edit Tour" : "Add Tour"}
                  </h4>
                </div>
                <form onSubmit={handleSubmit} className="flex flex-col p-6">
                  {loading ? (
                    <div className="space-y-4">
                      {Array(5)
                        .fill()
                        .map((_, idx) => (
                          <div
                            key={idx}
                            className="animate-pulse bg-gray-200 dark:bg-gray-700 h-16 rounded-md"
                          ></div>
                        ))}
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCorners}
                      onDragStart={handleDragStart}
                      onDragMove={handleDragMove}
                      onDragEnd={handleDragEnd}
                      onDragCancel={handleDragCancel}
                    >
                      <SortableContext
                        id="root"
                        items={fields.allFields.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {areAllFieldsRemoved() ? (
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                            No fields have been added. Please add fields using
                            the buttons below.
                          </p>
                        ) : (
                          fields.allFields.map((item) => (
                            <TreeItem
                              key={item.id}
                              item={item}
                              depth={0}
                              removeField={removeField}
                              allFieldsItems={allFieldsItems}
                              activeId={activeId}
                              projected={projected}
                            />
                          ))
                        )}
                      </SortableContext>
                      <DragOverlay>
                        {activeId && activeItem ? (
                          <FieldInput
                            id={activeItem.id}
                            type={activeItem.type}
                            label={activeItem.label}
                            index={null}
                            placeholder={activeItem.placeholder}
                            isTextarea={activeItem.isTextarea}
                            removeField={() => {}}
                            dragListeners={{}}
                            depth={
                              projected ? projected.depth : activeItem.depth
                            }
                            data={activeItem.data || {}}
                          />
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  )}

                  <SEOSection formData={seoData} onChange={handleSeoChange} />

                  <div className="flex justify-end mt-6">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                      disabled={areAllFieldsRemoved() || loading}
                    >
                      {isEdit ? "Update" : "Submit"}
                    </button>
                  </div>
                </form>
                <div className="p-6 pt-0">
                  <div className="mt-8">
                    <h5 className="font-semibold text-base text-gray-700 dark:text-gray-300 mb-3">
                      Add Fields
                    </h5>
                    <div className="flex flex-wrap gap-3">
                      {Object.keys(fieldConfigs).map((fieldType) => (
                        <button
                          key={fieldType}
                          onClick={() => addField(fieldType)}
                          className="px-4 py-2 bg-transparent border border-primary-500 text-primary-500 rounded-md hover:bg-primary-500 hover:text-white"
                        >
                          Add {fieldConfigs[fieldType].label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
